import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Flag, Plus, Trash2, Users } from 'lucide-react';
import {
  STABLEFORD_POINT_CATEGORIES,
  standardStablefordConfig,
  type CourseImportHole,
  type CourseImportRequest,
  type StablefordConfig,
  type StablefordPointCategory,
} from '@golf-app/shared';
import { useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { useCourseLookup } from '@/hooks/useCourseLookup';
import { CourseLookupStatus } from '@/components/CourseLookupStatus';
import type { CourseResult } from '@/lib/courseLookup';

interface TeeDraft {
  key: string;
  name: string;
  slope: number;
  rating: number;
  parTotal: number;
  holes: CourseImportHole[];
}

interface SelectedCourse {
  name: string;
  city: string | null;
  state: string | null;
  tees: TeeDraft[];
}

interface CatalogTee {
  tee_name: string;
  slope_rating: number;
  course_rating: number;
  par_total: number;
  holes?: Array<{
    hole_number?: number;
    par: number;
    handicap?: number;
    yards?: number | null;
  }>;
}

interface PlayerDraft {
  clientPlayerId: string;
  nickname: string;
  handicapIndex: string;
}

type PointDrafts = Record<StablefordPointCategory, string>;

const POINT_LABELS: Record<StablefordPointCategory, string> = {
  albatrossOrBetter: 'Albatross or better',
  eagle: 'Eagle',
  birdie: 'Birdie',
  par: 'Par',
  bogey: 'Bogey',
  doubleBogeyOrWorse: 'Double bogey or worse',
};

function uniqueId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function defaultHoles(parTotal: number): CourseImportHole[] {
  const holeCount = parTotal < 54 ? 9 : 18;
  const layouts: Record<number, number[]> = {
    72: [4, 4, 3, 5, 4, 4, 3, 5, 4, 4, 4, 3, 5, 4, 4, 3, 5, 4],
    71: [4, 4, 3, 5, 4, 4, 3, 5, 4, 4, 4, 3, 5, 4, 4, 3, 4, 4],
    70: [4, 4, 3, 5, 4, 4, 3, 4, 4, 4, 4, 3, 5, 4, 4, 3, 4, 4],
  };
  const pars = layouts[parTotal]?.slice(0, holeCount) ?? (() => {
    const base = Math.floor(parTotal / holeCount);
    let remainder = parTotal - base * holeCount;
    return Array.from({ length: holeCount }, () => {
      if (remainder > 0) {
        remainder -= 1;
        return base + 1;
      }
      return base;
    });
  })();
  return pars.map((par, index) => ({ number: index + 1, par, handicap: index + 1 }));
}

function teeFromCatalog(tee: CatalogTee, index: number): TeeDraft {
  const holes = tee.holes?.length
    ? tee.holes.map((hole, holeIndex) => ({
        number: hole.hole_number ?? holeIndex + 1,
        par: hole.par,
        handicap: Math.min(18, Math.max(1, hole.handicap ?? holeIndex + 1)),
        ...(hole.yards == null ? {} : { yards: hole.yards }),
      }))
    : defaultHoles(tee.par_total);
  return {
    key: `catalog-tee-${index}`,
    name: tee.tee_name,
    slope: tee.slope_rating,
    rating: tee.course_rating,
    parTotal: tee.par_total,
    holes,
  };
}

function standardPointDrafts(): PointDrafts {
  const points = standardStablefordConfig().points;
  return {
    albatrossOrBetter: String(points.albatrossOrBetter),
    eagle: String(points.eagle),
    birdie: String(points.birdie),
    par: String(points.par),
    bogey: String(points.bogey),
    doubleBogeyOrWorse: String(points.doubleBogeyOrWorse),
  };
}

function pointValues(drafts: PointDrafts): StablefordConfig['points'] | null {
  const parsed = STABLEFORD_POINT_CATEGORIES.map((key) => {
    const value = drafts[key];
    if (!/^-?\d+$/.test(value)) return null;
    const number = Number(value);
    return Number.isInteger(number) && number >= -99 && number <= 99 ? number : null;
  });
  if (parsed.some((value) => value == null)) return null;
  return {
    albatrossOrBetter: parsed[0]!,
    eagle: parsed[1]!,
    birdie: parsed[2]!,
    par: parsed[3]!,
    bogey: parsed[4]!,
    doubleBogeyOrWorse: parsed[5]!,
  };
}

export default function ScorecardSetup() {
  const navigate = useNavigate();
  const { displayName, username } = useAuthStore();
  const initialName = displayName ?? username ?? 'Golfer';
  const [players, setPlayers] = useState<PlayerDraft[]>(() => [{
    clientPlayerId: uniqueId('player'),
    nickname: initialName,
    handicapIndex: '0',
  }]);
  const [courseMode, setCourseMode] = useState<'search' | 'manual'>('search');
  const courseLookup = useCourseLookup<SelectedCourse>();
  const { query, results, selected: selectedCourse, lookup } = courseLookup;
  const [teeKey, setTeeKey] = useState('');
  const [manualCourse, setManualCourse] = useState({
    name: '', city: '', state: '', teeName: 'White', slope: '', rating: '', par: '72',
  });
  const [stablefordConfig, setStablefordConfig] = useState<StablefordConfig | null>(null);
  const [pointDrafts, setPointDrafts] = useState<PointDrafts>(standardPointDrafts);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startRequest = useRef<{ fingerprint: string; id: string } | null>(null);

  useEffect(() => { setTeeKey(selectedCourse?.tees[0]?.key ?? ''); }, [selectedCourse]);

  const selectedTee = selectedCourse?.tees.find((tee) => tee.key === teeKey) ?? null;
  const manualSlope = Number(manualCourse.slope);
  const manualRating = Number(manualCourse.rating);
  const manualPar = Number(manualCourse.par);
  const manualValid = manualCourse.name.trim().length > 0
    && manualCourse.teeName.trim().length > 0
    && Number.isInteger(manualSlope) && manualSlope >= 55 && manualSlope <= 155
    && Number.isFinite(manualRating) && manualRating >= 50 && manualRating <= 90
    && Number.isInteger(manualPar) && manualPar >= 27 && manualPar <= 80;
  const playerValid = players.length > 0 && players.length <= 12 && players.every((player, index) => {
    const handicap = Number(player.handicapIndex);
    return player.nickname.trim().length > 0
      && player.nickname.trim().length <= 80
      && player.handicapIndex.trim().length > 0
      && Number.isFinite(handicap)
      && handicap >= -10
      && handicap <= 54
      && players.findIndex((other) => (
        other.nickname.trim().toLocaleLowerCase() === player.nickname.trim().toLocaleLowerCase()
      )) === index;
  });
  const parsedPoints = useMemo(() => pointValues(pointDrafts), [pointDrafts]);
  const stablefordValid = stablefordConfig == null || parsedPoints != null;
  const courseValid = courseMode === 'search' ? selectedTee != null : manualValid;
  const canStart = playerValid && courseValid && stablefordValid && !submitting;

  function addPlayer() {
    if (players.length >= 12) return;
    setPlayers((current) => [...current, {
      clientPlayerId: uniqueId('player'), nickname: '', handicapIndex: '0',
    }]);
  }

  function updatePlayer(clientPlayerId: string, patch: Partial<PlayerDraft>) {
    setPlayers((current) => current.map((player) => (
      player.clientPlayerId === clientPlayerId ? { ...player, ...patch } : player
    )));
  }

  function removePlayer(clientPlayerId: string) {
    if (players.length === 1) return;
    setPlayers((current) => current.filter((player) => player.clientPlayerId !== clientPlayerId));
  }

  function changeCourseMode(mode: 'search' | 'manual') {
    lookup.setEnabled(mode === 'search');
    setCourseMode(mode);
    setError(null);
  }

  async function chooseCourse(course: CourseResult) {
    setError(null);
    await lookup.select(course, (row, full) => {
      if (full) {
        const tees = full.tees.map((tee, index) => ({
          key: `local-tee-${index}`,
          name: tee.name,
          slope: tee.slope,
          rating: tee.rating,
          parTotal: tee.parTotal,
          holes: (tee.holes?.length ? tee.holes : full.holes).map((hole) => ({
            number: hole.holeNumber,
            par: hole.par,
            handicap: hole.strokeIndex,
            ...(hole.yards == null ? {} : { yards: hole.yards }),
          })),
        })).filter((tee) => tee.holes.length === 9 || tee.holes.length === 18);
        if (tees.length === 0) throw new Error('This course has no complete scorecard tee to use.');
        return { name: full.name, city: full.city, state: full.state, tees };
      }
      const catalog = row.catalog;
      if (!catalog) throw new Error('That catalog course is no longer available. Search again.');
      const sourceTees = catalog.tees?.male?.length
        ? catalog.tees.male
        : catalog.tees?.female ?? [];
      const tees = sourceTees.map(teeFromCatalog).filter((tee) => (
        tee.holes.length === 9 || tee.holes.length === 18
      ));
      if (tees.length === 0) throw new Error('This course has no complete scorecard tee to use.');
      return { name: row.name, city: row.city, state: row.state, tees };
    });
  }

  function updateStablefordPoint(key: StablefordPointCategory, value: string) {
    const next = { ...pointDrafts, [key]: value };
    setPointDrafts(next);
    const points = pointValues(next);
    if (stablefordConfig && points) setStablefordConfig({ ...stablefordConfig, points });
  }

  function toggleStableford() {
    if (stablefordConfig) {
      setStablefordConfig(null);
      return;
    }
    const standard = standardStablefordConfig();
    setPointDrafts(standardPointDrafts());
    setStablefordConfig(standard);
  }

  function resetStablefordPoints() {
    const standard = standardStablefordConfig();
    setPointDrafts(standardPointDrafts());
    setStablefordConfig({ ...standard, scoringBasis: stablefordConfig?.scoringBasis ?? standard.scoringBasis });
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!canStart) {
      setError('Add valid players and a complete course setup before starting the scorecard.');
      return;
    }
    setSubmitting(true);
    try {
      const importRequest: CourseImportRequest = courseMode === 'search' && selectedCourse && selectedTee
        ? {
            name: selectedCourse.name,
            city: selectedCourse.city,
            state: selectedCourse.state,
            selectedTeeName: selectedTee.name,
            tees: selectedCourse.tees.map((tee) => ({
              name: tee.name,
              slope: tee.slope,
              rating: tee.rating,
              parTotal: tee.parTotal,
              holes: tee.holes,
            })),
          }
        : {
            name: manualCourse.name.trim(),
            city: manualCourse.city.trim() || null,
            state: manualCourse.state.trim() || null,
            selectedTeeName: manualCourse.teeName.trim(),
            tees: [{
              name: manualCourse.teeName.trim(),
              slope: manualSlope,
              rating: manualRating,
              parTotal: manualPar,
              holes: defaultHoles(manualPar),
            }],
          };
      const imported = await api.importCourse(importRequest);
      const selectedTeeName = courseMode === 'search' && selectedTee
        ? selectedTee.name
        : manualCourse.teeName.trim();
      const importedTee = imported.tees.find((tee) => (
        tee.name.toLocaleLowerCase() === selectedTeeName.toLocaleLowerCase()
      )) ?? imported.tees[0];
      if (!importedTee) throw new Error('The selected tee could not be saved.');
      const config = stablefordConfig && parsedPoints
        ? { ...stablefordConfig, points: parsedPoints }
        : undefined;
      const requestBody = {
        courseId: imported.courseId,
        teeId: importedTee.id,
        players: players.map((player) => ({
          clientPlayerId: player.clientPlayerId,
          nickname: player.nickname.trim(),
          handicapIndex: Number(player.handicapIndex),
        })),
        ...(config ? { stablefordConfig: config } : {}),
      };
      const fingerprint = JSON.stringify(requestBody);
      if (startRequest.current?.fingerprint !== fingerprint) {
        startRequest.current = { fingerprint, id: uniqueId('scorecard-start') };
      }
      const result = await api.startScorecardRound({
        clientRequestId: startRequest.current.id,
        ...requestBody,
      });
      navigate(`/app/scorecards/${result.roundId}`, { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start this scorecard.');
      setSubmitting(false);
    }
  }

  return (
    <AppShell back={{ to: '/app/home', label: 'Home' }}>
      <section className="t-hero">
        <h1>Start a scorecard</h1>
        <p>Set up your players, course, and optional Stableford points before the first tee.</p>
      </section>

      {error ? <div className="t-error" role="alert">{error}</div> : null}

      <form className="t-form scorecard-setup" onSubmit={handleSubmit}>
        <section className="scorecard-setup-section">
          <div className="scorecard-setup-heading">
            <div>
              <span className="t-label">Players</span>
              <p>Add everyone whose score you want to track.</p>
            </div>
            <Users aria-hidden="true" />
          </div>
          <div className="scorecard-player-list">
            {players.map((player, index) => (
              <div className="scorecard-player-row" key={player.clientPlayerId}>
                <label className="scorecard-player-name">
                  <span className="t-label">{index === 0 ? 'You' : `Player ${index + 1}`}</span>
                  <input
                    className="t-input"
                    value={player.nickname}
                    onChange={(event) => updatePlayer(player.clientPlayerId, { nickname: event.target.value })}
                    maxLength={80}
                    placeholder="Name"
                    aria-label={`${index === 0 ? 'Your' : `Player ${index + 1}`} name`}
                  />
                </label>
                <label className="scorecard-player-handicap">
                  <span className="t-label">HCP</span>
                  <input
                    className="t-input"
                    value={player.handicapIndex}
                    onChange={(event) => updatePlayer(player.clientPlayerId, { handicapIndex: event.target.value })}
                    inputMode="decimal"
                    maxLength={5}
                    aria-label={`${player.nickname || `Player ${index + 1}`} handicap index`}
                  />
                </label>
                <button
                  type="button"
                  className="scorecard-icon-button"
                  onClick={() => removePlayer(player.clientPlayerId)}
                  disabled={players.length === 1}
                  aria-label={`Remove ${player.nickname || `player ${index + 1}`}`}
                >
                  <Trash2 aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="t-btn t-btn-secondary" onClick={addPlayer} disabled={players.length >= 12}>
            <Plus aria-hidden="true" /> Add player
          </button>
          {!playerValid ? <p className="scorecard-field-error">Use unique names and handicaps from -10 to 54.</p> : null}
        </section>

        <section className="scorecard-setup-section">
          <div className="scorecard-setup-heading">
            <div>
              <span className="t-label">Course</span>
              <p>Search the catalog or enter the card details yourself.</p>
            </div>
            <Flag aria-hidden="true" />
          </div>
          <div className="t-chip-row" role="tablist" aria-label="Course entry method">
            <button type="button" className={`t-chip ${courseMode === 'search' ? 't-chip-active' : ''}`} onClick={() => changeCourseMode('search')} role="tab" aria-selected={courseMode === 'search'}>Search</button>
            <button type="button" className={`t-chip ${courseMode === 'manual' ? 't-chip-active' : ''}`} onClick={() => changeCourseMode('manual')} role="tab" aria-selected={courseMode === 'manual'}>Enter manually</button>
          </div>
          {courseMode === 'search' ? (
            selectedCourse ? (
              <>
                <div className="scorecard-course-selected">
                  <div>
                    <strong>{selectedCourse.name}</strong>
                    {(selectedCourse.city || selectedCourse.state) ? <span>{[selectedCourse.city, selectedCourse.state].filter(Boolean).join(', ')}</span> : null}
                  </div>
                  <button type="button" className="t-btn t-btn-ghost" onClick={() => { lookup.clear(); setTeeKey(''); }}>Change</button>
                </div>
                <div className="scorecard-tee-list" role="radiogroup" aria-label="Tee">
                  {selectedCourse.tees.map((tee) => (
                    <button
                      key={tee.key}
                      type="button"
                      className={`scorecard-tee-option ${teeKey === tee.key ? 'scorecard-tee-option-active' : ''}`}
                      onClick={() => setTeeKey(tee.key)}
                      role="radio"
                      aria-checked={teeKey === tee.key}
                    >
                      <strong>{tee.name}</strong>
                      <span>{tee.parTotal} par · {tee.rating.toFixed(1)} / {tee.slope}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <input className="t-input" value={query} onChange={(event) => lookup.setQuery(event.target.value)} autoComplete="off" autoCorrect="off" spellCheck={false} placeholder="Search course or city" aria-label="Search course or city" />
                <CourseLookupStatus state={courseLookup} onRetry={lookup.retry} />
                {results.length > 0 ? (
                  <div className="t-list scorecard-course-results">
                    {results.map((course) => (
                      <button type="button" className="t-row" key={`${course.source}-${course.id}`} onClick={() => void chooseCourse(course)}>
                        <span className="t-row-body">
                          <span className="t-row-name">{course.name}</span>
                          {(course.city || course.state) ? <span className="t-row-sub">{[course.city, course.state].filter(Boolean).join(', ')}</span> : null}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )
          ) : (
            <div className="scorecard-manual-course">
              <label className="t-field"><span className="t-label">Course name</span><input className="t-input" value={manualCourse.name} onChange={(event) => setManualCourse({ ...manualCourse, name: event.target.value })} placeholder="Course name" /></label>
              <div className="t-field-row">
                <label className="t-field"><span className="t-label">City</span><input className="t-input" value={manualCourse.city} onChange={(event) => setManualCourse({ ...manualCourse, city: event.target.value })} placeholder="Optional" /></label>
                <label className="t-field"><span className="t-label">State</span><input className="t-input" value={manualCourse.state} onChange={(event) => setManualCourse({ ...manualCourse, state: event.target.value })} placeholder="Optional" maxLength={3} /></label>
              </div>
              <div className="t-field-row">
                <label className="t-field"><span className="t-label">Tee</span><input className="t-input" value={manualCourse.teeName} onChange={(event) => setManualCourse({ ...manualCourse, teeName: event.target.value })} /></label>
                <label className="t-field"><span className="t-label">Slope</span><input className="t-input" value={manualCourse.slope} onChange={(event) => setManualCourse({ ...manualCourse, slope: event.target.value })} inputMode="numeric" placeholder="113" /></label>
                <label className="t-field"><span className="t-label">Rating</span><input className="t-input" value={manualCourse.rating} onChange={(event) => setManualCourse({ ...manualCourse, rating: event.target.value })} inputMode="decimal" placeholder="72.0" /></label>
                <label className="t-field"><span className="t-label">Par</span><input className="t-input" value={manualCourse.par} onChange={(event) => setManualCourse({ ...manualCourse, par: event.target.value })} inputMode="numeric" /></label>
              </div>
              {!manualValid ? <p className="scorecard-field-error">Enter a course, tee, slope, rating, and par to create its scorecard.</p> : null}
            </div>
          )}
        </section>

        <section className="scorecard-setup-section scorecard-stableford-card">
          <div className="scorecard-setup-heading">
            <div>
              <span className="t-label">Scoring format</span>
              <p>Stableford awards points on every hole against par.</p>
            </div>
            <button type="button" className={`scorecard-toggle ${stablefordConfig ? 'scorecard-toggle-active' : ''}`} onClick={toggleStableford} role="switch" aria-checked={stablefordConfig != null}>
              {stablefordConfig ? 'On' : 'Off'}
            </button>
          </div>
          {stablefordConfig ? (
            <div className="scorecard-stableford-body">
              <div className="t-chip-row" role="radiogroup" aria-label="Stableford scoring basis">
                {(['gross', 'net'] as const).map((basis) => (
                  <button key={basis} type="button" className={`t-chip ${stablefordConfig.scoringBasis === basis ? 't-chip-active' : ''}`} onClick={() => setStablefordConfig({ ...stablefordConfig, scoringBasis: basis })} role="radio" aria-checked={stablefordConfig.scoringBasis === basis}>
                    {basis === 'gross' ? 'Gross' : 'Net'}
                  </button>
                ))}
              </div>
              <p className="t-hint">{stablefordConfig.scoringBasis === 'net' ? 'Net points apply each player’s course handicap by hole stroke index.' : 'Gross points use the score entered on each hole.'}</p>
              <div className="scorecard-points-table">
                {STABLEFORD_POINT_CATEGORIES.map((key) => {
                  const valid = /^-?\d+$/.test(pointDrafts[key]) && Number(pointDrafts[key]) >= -99 && Number(pointDrafts[key]) <= 99;
                  return (
                    <label className="scorecard-point-row" key={key}>
                      <span>{POINT_LABELS[key]}</span>
                      <input className={`scorecard-point-input ${valid ? '' : 'scorecard-point-input-error'}`} value={pointDrafts[key]} onChange={(event) => updateStablefordPoint(key, event.target.value)} inputMode="numeric" maxLength={3} aria-label={`${POINT_LABELS[key]} point value`} />
                    </label>
                  );
                })}
              </div>
              {!stablefordValid ? <p className="scorecard-field-error">Every point value must be a whole number from -99 to 99.</p> : null}
              <button type="button" className="scorecard-reset-link" onClick={resetStablefordPoints}>Use standard values</button>
            </div>
          ) : null}
        </section>

        <button type="submit" className="t-btn t-btn-primary t-btn-block" disabled={!canStart}>
          {submitting ? 'Starting scorecard…' : 'Start scorecard'}
        </button>
      </form>
    </AppShell>
  );
}
